import type { IsoTimestamp, SyncedRow, SyncedTable, Uuid } from "../types/base.js";

/**
 * Ein Eintrag der Outbox. Jede Änderung der Oberfläche schreibt zuerst hierhin
 * (Regel 1), der Sync arbeitet sie später ab. Die Reihenfolge ist die Reihenfolge
 * der Erzeugung, damit "anlegen" vor "abhaken" beim Server ankommt.
 */
/**
 * Wie eine Zeile beim Server ankommt.
 *
 * `upsert` ist der Normalfall: die Zeile entsteht oder wird ersetzt.
 * `update` gibt es für Tabellen, in die niemand einfügen darf. `families` ist
 * so ein Fall: Familien entstehen ausschließlich über create_family(), damit
 * nie eine Familie ohne Eigentümer existiert. Ein Upsert würde dort an der
 * fehlenden insert-Policy scheitern, obwohl nur geändert werden soll.
 */
export type OutboxOperation = "upsert" | "update";

export interface OutboxEntry<T extends SyncedRow = SyncedRow> {
  /** Vom Client erzeugte UUID, damit ein doppelter Upload nichts kaputt macht. */
  id: Uuid;
  table: SyncedTable;
  operation?: OutboxOperation;
  row_id: Uuid;
  /** Vollständige Zeile, kein Delta: der Upload ist ein Upsert. */
  payload: T;
  client_updated_at: IsoTimestamp;
  attempts: number;
  /** Zeitpunkt des nächsten Versuchs, gesetzt vom Backoff nach einem Fehler. */
  next_attempt_at: IsoTimestamp;
  last_error: string | null;
}

export interface SyncCursor {
  table: SyncedTable;
  /** Höchstes gesehenes `updated_at`. Der Pull holt alles, was jünger ist. */
  updated_at: IsoTimestamp | null;
}

export type SyncPhase = "idle" | "push" | "pull" | "error";

export interface SyncState {
  phase: SyncPhase;
  online: boolean;
  pending: number;
  last_success_at: IsoTimestamp | null;
  last_error: string | null;
}

/**
 * Der lokale Speicher. Im Web ist das Dexie, unter Expo später expo-sqlite
 * (Prompt 6). Der Kern kennt nur dieses Interface.
 */
export interface StorageAdapter {
  readRows<T extends SyncedRow>(table: SyncedTable, ids: readonly Uuid[]): Promise<T[]>;
  writeRows<T extends SyncedRow>(table: SyncedTable, rows: readonly T[]): Promise<void>;
  enqueue(entry: OutboxEntry): Promise<void>;
  takeDue(now: IsoTimestamp, limit: number): Promise<OutboxEntry[]>;
  markSent(ids: readonly Uuid[]): Promise<void>;
  markFailed(id: Uuid, error: string, nextAttemptAt: IsoTimestamp): Promise<void>;
  readCursor(table: SyncedTable): Promise<SyncCursor>;
  writeCursor(cursor: SyncCursor): Promise<void>;
  countPending(): Promise<number>;
}

/** Die Gegenstelle, im Web Supabase. Getrennt, damit Tests ohne Netz laufen. */
/**
 * Die Verbindung hat versagt, nicht die Daten.
 *
 * Der Unterschied entscheidet, was die Engine danach tut. Lehnt der Server
 * eine Zeile ab (4xx), lohnt es, die übrigen Zeilen einzeln zu probieren.
 * Ist das Netz weg oder der Server überlastet (5xx), wäre jeder Einzelversuch
 * genauso vergeblich, und 39 wartende Artikel würden 39 vergebliche Anfragen
 * je Runde bedeuten.
 */
export class TransportError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = "TransportError";
  }
}

export interface RemoteAdapter {
  upsert<T extends SyncedRow>(table: SyncedTable, rows: readonly T[]): Promise<void>;
  /** Ändert vorhandene Zeilen, ohne sie anlegen zu dürfen. */
  update<T extends SyncedRow>(table: SyncedTable, rows: readonly T[]): Promise<void>;
  fetchSince<T extends SyncedRow>(table: SyncedTable, since: IsoTimestamp | null, limit: number): Promise<T[]>;
}
